import React, { useState, useEffect } from 'react';
import api from '../services/api';

const GridView = ({ selectedScheme, selectedInstitute }) => {
  // State for view type selection (training/infra/admin)
  const [viewType, setViewType] = useState('training');
  const [trainingData, setTrainingData] = useState([]);
  const [infraData, setInfraData] = useState([]);
  const [adminData, setAdminData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Add this helper function after your state declarations
  const filterDataByScheme = (data, selectedScheme) => {
    if (!data || data.length === 0) return [];
    if (selectedScheme === 'ALL') return data;
    return data.filter(item => item.scheme === selectedScheme);
  };

  // Add this helper function after filterDataByScheme
  const filterDataByInstitute = (data, selectedInstitute) => {
    if (!data || data.length === 0) return [];
    if (selectedInstitute === 'ALL') return data;
    return data.filter(item => item.institute === selectedInstitute);
  };

  // Combined filter function for both scheme and institute
  const filterData = (data) => {
    let filteredData = data;
    
    // Apply scheme filter first if scheme is selected
    if (selectedScheme) {
      filteredData = filterDataByScheme(filteredData, selectedScheme);
    }
    
    // Apply institute filter if institute is selected
    if (selectedInstitute) {
      filteredData = filterDataByInstitute(filteredData, selectedInstitute);
    }
    
    return filteredData;
  };

  // Fetch data based on selected view type
  useEffect(() => {
    // Clear any existing errors when switching views
    setError(null);
    
    if (viewType === 'training') {
      fetchTrainingData();
    } else if (viewType === 'infra') {
      fetchInfraData();
    } else if (viewType === 'admin') {
      fetchAdminData();
    }
  }, [viewType]);

  // Fetch training data from API
  const fetchTrainingData = async () => {
    try {
      setLoading(true);
      const response = await api.training.getTrainingList();
      
      if (response && !response.error) {
        // Process the training data to ensure all items have a status
        const processedData = (response.data || []).map(training => ({
          ...training,
          status: training.status || 'Awaiting Approval' // Default status if not provided
        }));
        setTrainingData(processedData);
      } else {
        setError(response.message || 'Failed to fetch trainings');
      }
    } catch (err) {
      setError('Error fetching training data: ' + (err.message || 'Unknown error'));
      console.error('Error fetching training data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch infrastructure data from API
  const fetchInfraData = async () => {
    try {
      setLoading(true);
      // Using the correct API endpoint for infrastructure
      const response = await fetch(`${api.BASE_URL}/project/infra/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch infrastructure data');
      }
      
      const data = await response.json();
      
      if (data && !data.error) {
        // Process the infrastructure data
        const processedData = data.data || [];
        setInfraData(processedData);
      } else {
        setError(data.message || 'Failed to fetch infrastructure data');
      }
    } catch (err) {
      setError('Error fetching infrastructure data: ' + (err.message || 'Unknown error'));
      console.error('Error fetching infrastructure data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch admin data from API
  const fetchAdminData = async () => {
    try {
      setLoading(true);
      // Using the correct API endpoint for admin
      const response = await fetch(`${api.BASE_URL}/project/admin/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch admin data');
      }
      
      const data = await response.json();
      
      if (data && !data.error) {
        // Process the admin data
        const processedData = (data.data || []).map(admin => ({
          ...admin,
          status: admin.status || 'Pending' // Default status if not provided
        }));
        setAdminData(processedData);
      } else {
        setError(data.message || 'Failed to fetch admin data');
      }
    } catch (err) {
      // Only set error if we're still on the admin view
      if (viewType === 'admin') {
        setError('Error fetching admin data: ' + (err.message || 'Unknown error'));
        console.error('Error fetching admin data:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to safely render clientile data
  const renderClientile = (clientile) => {
    if (!clientile) return 'N/A';
    
    // If clientile is already a string
    if (typeof clientile === 'string') {
      return clientile.length > 50
        ? `${clientile.substring(0, 50)}...`
        : clientile;
    }
    
    // If clientile is an array
    if (Array.isArray(clientile)) {
      return clientile.map(client => 
        `${client.typeLabel}: ${client.member} (${client.nos})`
      ).join(', ');
    }
    
    // If clientile might be a JSON string
    try {
      if (typeof clientile === 'string' && clientile.startsWith('[')) {
        const parsed = JSON.parse(clientile);
        return Array.isArray(parsed) ? 'Multiple client types' : 'Client data';
      }
    } catch (e) {
      // If parsing fails, just return a failsafe
      return 'Client data available';
    }
    
    // If clientile is an object
    return 'Client data available';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return '₹' + Number(amount).toLocaleString();
  };

  // Get the date range for a training
  const getDateRange = (training) => {
    if (!training.batchesData || training.batchesData.length === 0) {
      return null;
    }
    
    // Sort batches by fromDate
    const sortedBatches = [...training.batchesData].sort((a, b) => 
      new Date(a.fromDate) - new Date(b.fromDate)
    );
    
    const firstBatchStart = sortedBatches[0].fromDate;
    
    // Sort batches by toDate to get the latest end date
    const batchesByEndDate = [...training.batchesData].sort((a, b) => 
      new Date(b.toDate) - new Date(a.toDate)
    );
    
    const lastBatchEnd = batchesByEndDate[0].toDate;
    
    return { fromDate: firstBatchStart, toDate: lastBatchEnd };
  };
  
  // Generate code for display based on item type
  const generateItemCode = (item) => {
    if (viewType === 'training') {
      return `${item.institute || ''}/${item.scheme || ''}/${item.center || ''}/${item.year || ''}/${item.month || ''}/${item.stitle || ''}/${item.sno || ''}`;
    } else if (viewType === 'infra') {
      return `${item.institute || ''}/WOR/${item.scheme || ''}/${item.center || ''}/${item.year || ''}/${item.month || ''}/${item.stitle || ''}/${item.sno || ''}`;
    } else {
      // For admin, create a custom format with ADM
      return `${item.institute || ''}/ADM/${item.scheme || ''}/${item.center || ''}/${item.year || ''}/${item.month || ''}/${item.stitle || ''}/${item.sno || ''}`;
    }
  };

  // Render a training card
  const renderTrainingCard = (training, index) => {
    const dateRange = getDateRange(training);
    const isHovered = hoveredItem === `training-${training.id}`;
    
    return (
      <div 
        key={training.id || index} 
        className={`border rounded-lg overflow-hidden transition-all duration-300 ${
          isHovered ? 'shadow-lg transform scale-[1.02]' : 'shadow hover:shadow-md'
        }`}
        onMouseEnter={() => setHoveredItem(`training-${training.id}`)}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <div className={`p-3 relative ${
          training.institute === 'SIR' ? 'bg-blue-50' :
          training.institute === 'RIR' ? 'bg-green-50' :
          training.institute === 'DRC' ? 'bg-purple-50' :
          'bg-gray-50'
        }`}>
          {/* Animated border effect on hover */}
          {isHovered && (
            <div className="absolute inset-0 border-2 rounded-t-lg animate-borderFlow" style={{
              borderImage: training.institute === 'SIR' ? 'linear-gradient(90deg, #3b82f6, #93c5fd, #3b82f6) 1' :
                        training.institute === 'RIR' ? 'linear-gradient(90deg, #10b981, #a7f3d0, #10b981) 1' :
                        training.institute === 'DRC' ? 'linear-gradient(90deg, #8b5cf6, #c4b5fd, #8b5cf6) 1' :
                        'linear-gradient(90deg, #6b7280, #e5e7eb, #6b7280) 1',
              borderImageSlice: '1',
            }}></div>
          )}

          <h3 className="font-medium truncate relative z-10">
            {training.title || 'Untitled Training'}
          </h3>
          <div className="flex justify-between mt-1 relative z-10">
            <span className="text-xs bg-white px-2 py-0.5 rounded shadow-sm">
              {training.type}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded shadow-sm ${
              training.institute === 'SIR' ? 'bg-blue-100 text-blue-800' :
              training.institute === 'RIR' ? 'bg-green-100 text-green-800' :
              training.institute === 'DRC' ? 'bg-purple-100 text-purple-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {training.institute}
            </span>
          </div>
        </div>
        
        <div className="p-3">
          <div className="text-sm text-gray-600 mb-2">
            {renderClientile(training.clientile)}
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">Scheme:</span> {training.scheme || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Year:</span> {training.year || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Batches:</span> {training.batches || (training.batchesData ? training.batchesData.length : '0')}
            </div>
            <div>
              <span className="text-gray-500">Participants:</span> {training.participants || '0'}
            </div>
          </div>
          
          {dateRange && (
            <div className="mt-3 pt-3 border-t">
              <div className="text-xs font-medium mb-1">Schedule:</div>
              <div className="text-xs">
                From: {formatDate(dateRange.fromDate)}
                <br />
                To: {formatDate(dateRange.toDate)}
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t flex justify-between items-center">
            <span className={`text-xs px-2 py-1 rounded-full ${
              training.status === 'Approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {training.status || 'Awaiting Approval'}
            </span>
            
            <div className="text-xs text-gray-500">
              {generateItemCode(training)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render an infrastructure card
  const renderInfraCard = (infra, index) => {
    const isHovered = hoveredItem === `infra-${infra.id}`;
    
    return (
      <div 
        key={infra.id || index} 
        className={`border rounded-lg overflow-hidden transition-all duration-300 ${
          isHovered ? 'shadow-lg transform scale-[1.02]' : 'shadow hover:shadow-md'
        }`}
        onMouseEnter={() => setHoveredItem(`infra-${infra.id}`)}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <div className={`p-3 relative ${
          infra.institute === 'SIR' ? 'bg-blue-50' :
          infra.institute === 'RIR' ? 'bg-green-50' :
          infra.institute === 'DRC' ? 'bg-purple-50' :
          'bg-gray-50'
        }`}>
          {/* Animated border effect on hover */}
          {isHovered && (
            <div className="absolute inset-0 border-2 rounded-t-lg animate-borderFlow" style={{
              borderImage: infra.institute === 'SIR' ? 'linear-gradient(90deg, #3b82f6, #93c5fd, #3b82f6) 1' :
                         infra.institute === 'RIR' ? 'linear-gradient(90deg, #10b981, #a7f3d0, #10b981) 1' :
                         infra.institute === 'DRC' ? 'linear-gradient(90deg, #8b5cf6, #c4b5fd, #8b5cf6) 1' :
                         'linear-gradient(90deg, #6b7280, #e5e7eb, #6b7280) 1',
              borderImageSlice: '1',
            }}></div>
          )}

          <h3 className="font-medium truncate relative z-10">
            {infra.title || 'Untitled Infrastructure'}
          </h3>
          <div className="flex justify-between mt-1 relative z-10">
            <span className="text-xs bg-white px-2 py-0.5 rounded shadow-sm">
              {infra.type || 'Unknown Type'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded shadow-sm ${
              infra.institute === 'SIR' ? 'bg-blue-100 text-blue-800' :
              infra.institute === 'RIR' ? 'bg-green-100 text-green-800' :
              infra.institute === 'DRC' ? 'bg-purple-100 text-purple-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {infra.institute}
            </span>
          </div>
        </div>
        
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">Scheme:</span> {infra.scheme || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Center:</span> {infra.center || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Year:</span> {infra.year || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Units:</span> {infra.units || '0'}
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t">
            <div className="flex justify-between">
              <div className="text-sm font-medium">Amount:</div>
              <div className="text-sm font-bold text-blue-600">{formatCurrency(infra.amount)}</div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t flex justify-between items-center">
            <span className={`text-xs px-2 py-1 rounded-full ${
              infra.status === 'Approved' ? 'bg-green-100 text-green-800' : 
              infra.status === 'Active' ? 'bg-blue-100 text-blue-800' : 
              infra.status === 'Completed' ? 'bg-purple-100 text-purple-800' : 
              'bg-yellow-100 text-yellow-800'
            }`}>
              {infra.status || 'Awaiting Approval'}
            </span>
            
            <div className="text-xs text-gray-500">
              {generateItemCode(infra)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render an admin card
  const renderAdminCard = (admin, index) => {
    const isHovered = hoveredItem === `admin-${admin.id}`;
    
    return (
      <div 
        key={admin.id || index} 
        className={`border rounded-lg overflow-hidden transition-all duration-300 ${
          isHovered ? 'shadow-lg transform scale-[1.02]' : 'shadow hover:shadow-md'
        }`}
        onMouseEnter={() => setHoveredItem(`admin-${admin.id}`)}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <div className={`p-3 relative ${
          admin.institute === 'SIR' ? 'bg-blue-50' :
          admin.institute === 'RIR' ? 'bg-green-50' :
          admin.institute === 'DRC' ? 'bg-purple-50' :
          'bg-gray-50'
        }`}>
          {/* Animated border effect on hover */}
          {isHovered && (
            <div className="absolute inset-0 border-2 rounded-t-lg animate-borderFlow" style={{
              borderImage: admin.institute === 'SIR' ? 'linear-gradient(90deg, #3b82f6, #93c5fd, #3b82f6) 1' :
                         admin.institute === 'RIR' ? 'linear-gradient(90deg, #10b981, #a7f3d0, #10b981) 1' :
                         admin.institute === 'DRC' ? 'linear-gradient(90deg, #8b5cf6, #c4b5fd, #8b5cf6) 1' :
                         'linear-gradient(90deg, #6b7280, #e5e7eb, #6b7280) 1',
              borderImageSlice: '1',
            }}></div>
          )}

          <h3 className="font-medium truncate relative z-10">
            {admin.title || 'Untitled Task'}
          </h3>
          <div className="flex justify-between mt-1 relative z-10">
            <span className="text-xs bg-white px-2 py-0.5 rounded shadow-sm">
              {admin.type || 'General'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded shadow-sm ${
              admin.institute === 'SIR' ? 'bg-blue-100 text-blue-800' :
              admin.institute === 'RIR' ? 'bg-green-100 text-green-800' :
              admin.institute === 'DRC' ? 'bg-purple-100 text-purple-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {admin.institute}
            </span>
          </div>
        </div>
        
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">Scheme:</span> {admin.scheme_name || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Center:</span> {admin.center_name || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Year:</span> {admin.year || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Training Type:</span> {admin.training_type || 'N/A'}
            </div>
          </div>
          
          {admin.amount && (
            <div className="mt-3 pt-3 border-t">
              <div className="flex justify-between">
                <div className="text-sm font-medium">Amount:</div>
                <div className="text-sm font-bold text-blue-600">{formatCurrency(admin.amount)}</div>
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t flex justify-between items-center">
            <span className={`text-xs px-2 py-1 rounded-full ${
              admin.status === 'Active' ? 'bg-green-100 text-green-800' : 
              admin.status === 'Completed' ? 'bg-purple-100 text-purple-800' : 
              'bg-yellow-100 text-yellow-800'
            }`}>
              {admin.status || 'Pending'}
            </span>
            
            <div className="text-xs text-gray-500">
              {generateItemCode(admin)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get filter description for display
  const getFilterDescription = () => {
    let description = '';
    if (selectedScheme && selectedScheme !== 'ALL') {
      description += `scheme ${selectedScheme}`;
    } else if (selectedScheme === 'ALL') {
      description += 'all schemes';
    }
    
    if (selectedInstitute && selectedInstitute !== 'ALL') {
      if (description) description += ' and ';
      description += `institute ${selectedInstitute}`;
    } else if (selectedInstitute === 'ALL') {
      if (description) description += ' and ';
      description += 'all institutes';
    }
    
    return description || 'all data';
  };

  return (
    <div className="space-y-4">
      {/* View type toggle */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <div className="flex justify-center">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setViewType('training')}
              className={`px-5 py-2.5 text-sm font-medium rounded-l-lg border ${
                viewType === 'training'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Training
            </button>
            <button
              type="button"
              onClick={() => setViewType('infra')}
              className={`px-5 py-2.5 text-sm font-medium border ${
                viewType === 'infra'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Infrastructure
            </button>
            <button
              type="button"
              onClick={() => setViewType('admin')}
              className={`px-5 py-2.5 text-sm font-medium rounded-r-lg border ${
                viewType === 'admin'
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Admin
            </button>
          </div>
        </div>
      </div>

      {/* Filter indicator */}
      {(selectedScheme || selectedInstitute) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-blue-800 font-medium">Filtered by:</span>
            {selectedScheme && selectedScheme !== 'ALL' && (
              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                Scheme: {selectedScheme}
              </span>
            )}
            {selectedInstitute && selectedInstitute !== 'ALL' && (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                Institute: {selectedInstitute}
              </span>
            )}
            {selectedScheme === 'ALL' && (
              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                All Schemes
              </span>
            )}
            {selectedInstitute === 'ALL' && (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                All Institutes
              </span>
            )}
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-3 text-gray-500">Loading {
            viewType === 'training' ? 'trainings' : 
            viewType === 'infra' ? 'infrastructure projects' : 
            'admin data'
          }...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="text-center py-8 bg-red-50 rounded-lg border border-red-100">
          <svg className="h-10 w-10 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="mt-2 text-red-600">{error}</p>
          <button 
            onClick={viewType === 'training' ? fetchTrainingData : 
                    viewType === 'infra' ? fetchInfraData : fetchAdminData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md text-sm"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Grid display */}
      {!loading && !error && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {viewType === 'training' ? (
              (() => {
                const filteredData = filterData(trainingData);
                return filteredData.length > 0 ? (
                  filteredData.map((training, index) => renderTrainingCard(training, index))
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No trainings found for {getFilterDescription()}.
                  </div>
                );
              })()
            ) : viewType === 'infra' ? (
              (() => {
                const filteredData = filterData(infraData);
                return filteredData.length > 0 ? (
                  filteredData.map((infra, index) => renderInfraCard(infra, index))
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No infrastructure projects found for {getFilterDescription()}.
                  </div>
                );
              })()
            ) : (
              (() => {
                const filteredData = filterData(adminData);
                return filteredData.length > 0 ? (
                  filteredData.map((admin, index) => renderAdminCard(admin, index))
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No admin tasks found for {getFilterDescription()}.
                  </div>
                );
              })()
            )}
          </div>
          
          {/* Item count */}
          <div className="text-center text-sm text-gray-500 mt-4">
            Showing {
              (() => {
                if (viewType === 'training') {
                  return filterData(trainingData).length;
                } else if (viewType === 'infra') {
                  return filterData(infraData).length;
                } else {
                  return filterData(adminData).length;
                }
              })()
            } {
              viewType === 'training' ? 'training' : 
              viewType === 'infra' ? 'infrastructure' : 
              'admin'
            } {
              (() => {
                const count = viewType === 'training' ? filterData(trainingData).length : 
                             viewType === 'infra' ? filterData(infraData).length : 
                             filterData(adminData).length;
                return count === 1 ? 'item' : 'items';
              })()
            } for {getFilterDescription()}
          </div>
        </>
      )}

      {/* CSS for animated border */}
      <style jsx>{`
        @keyframes borderFlow {
          0% {
            background-position: 0% 0;
          }
          100% {
            background-position: 100% 0;
          }
        }
        .animate-borderFlow {
          animation: borderFlow 2s infinite linear;
        }
      `}</style>
    </div>
  );
};

export default GridView;